import { assert, FilterGroup } from '@fe/core';
import { FeMap, FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { ExpressionFilterSpecification, FilterSpecification, GeoJSONSource, Subscription } from 'maplibre-gl';
import { IUDViewStaticDataModel } from './navdata-types';

export class NavaidRingsLayer implements FeMapPlugin {
    private map: FeMap | null = null;
    private subscriptions = [] as Subscription[];
    private filterGroup: Partial<Record<string, FilterGroup>> = {};
    private featureCache = new Map<string, GeoJSON.Feature<GeoJSON.Polygon>>();

    onAdd(map: FeMap) {
        this.map = map;
        this.map.addSource('fe-navaid-rings', { type: 'geojson', data: turf.featureCollection([...this.featureCache.values()].flat()) });
        this.map.addLayer(
            {
                minzoom: 3,
                id: 'fe-navaid-rings-fill',
                source: 'fe-navaid-rings',
                type: 'fill',
                paint: {
                    'fill-color': 'rgba(0,0,255,0.2)'
                }
            },
            'fe-navaid-rings'
        );
        this.map.addLayer(
            {
                minzoom: 3,
                id: 'fe-navaid-rings-lines',
                source: 'fe-navaid-rings',
                type: 'line',
                paint: {
                    'line-color': 'rgba(0,0,255,1.0)',
                    'line-width': 1
                }
            },
            'fe-navaid-rings'
        );
        this.map.addLayer(
            {
                minzoom: 5,
                id: 'fe-navaid-rings-labels',
                source: 'fe-navaid-rings',
                type: 'symbol',
                layout: {
                    'text-field': ['get', 'label'],
                    'symbol-placement': 'line',
                    'text-font': ['Metropolis Regular'],
                    'text-size': 9,
                    'text-offset': [0, 0.7]
                },
                paint: {
                    'text-color': ['global-state', 'text-color']
                }
            },
            'fe-navaid-rings'
        );

        // Subscribe to idle only once. If we were to use this event to trigger updates it'll result in an infinite loop update -> idle -> update -> idle...
        const idleHandler = () => this.update();

        map.once('idle', idleHandler);
        this.subscriptions = [map.on('moveend', this.update.bind(this)), { unsubscribe: () => map.off('idle', idleHandler) }];
    }

    onRemove(map: FeMap) {
        this.subscriptions.forEach((x) => x.unsubscribe());
        this.map = null;
    }

    setFilterGroups(filterGroups: typeof this.filterGroup) {
        this.filterGroup = filterGroups;
    }

    apply(data: IUDViewStaticDataModel) {
        assert(this.map);
        this.map.setPaintProperty('fe-navaid-rings-lines', 'line-color', data.navaids.rings.lineColor);
        this.map.setPaintProperty('fe-navaid-rings-fill', 'fill-color', data.navaids.rings.fillColor);

        if (data.navaids.show && data.navaids.rings.draw) {
            this.update();
            this.map.setLayoutProperty('fe-navaid-rings-fill', 'visibility', 'visible');
            this.map.setLayoutProperty('fe-navaid-rings-lines', 'visibility', 'visible');
            this.map.setLayoutProperty('fe-navaid-rings-labels', 'visibility', 'visible');

            const filterRingType: FilterSpecification = [
                'in',
                ['get', 'power'],
                [
                    'literal',
                    [
                        data.navaids.rings.drawTerminal && 'T',
                        data.navaids.rings.drawLowAltitude && 'L',
                        data.navaids.rings.drawHighAltitude && 'H',
                        data.navaids.rings.drawUnrestricted && 'U'
                    ].filter(Boolean)
                ]
            ];

            const filterGroup = data.navaids.filterGroup;
            const filterGroupValues = filterGroup?.show
                ? filterGroup.ids
                      .map((x) => this.filterGroup[x])
                      .filter((x) => !!x)
                      .map((x) => x.members.map((x) => x.text))
                      .flat()
                : [];

            const filterFilterGroup: ExpressionFilterSpecification = ['in', ['get', 'vorId'], ['literal', filterGroupValues]];

            const typeFilter: ExpressionFilterSpecification = [
                'in',
                ['get', 'type'],
                ['literal', [data.navaids.vor && 4, data.navaids.voR_DME && 5, data.navaids.vortac && 6].filter(Boolean)]
            ];

            const mainFilter: ExpressionFilterSpecification = ['all', filterRingType, ['any', filterFilterGroup, typeFilter]];

            this.map.setFilter('fe-navaid-rings-fill', mainFilter);
            this.map.setFilter('fe-navaid-rings-lines', mainFilter);
            this.map.setFilter('fe-navaid-rings-labels', mainFilter);
        } else {
            this.map.setLayoutProperty('fe-navaid-rings-fill', 'visibility', 'none');
            this.map.setLayoutProperty('fe-navaid-rings-lines', 'visibility', 'none');
            this.map.setLayoutProperty('fe-navaid-rings-labels', 'visibility', 'none');
        }
    }

    private update() {
        assert(this.map);
        const added = [];
        for (const navaid of this.map.queryRenderedFeatures(undefined, {
            layers: ['navaid'],
            filter: ['in', ['get', 'type'], ['literal', [4, 5, 6]]]
        })) {
            if (this.featureCache.has(navaid.properties.id)) {
                continue;
            }

            const geometry = navaid.geometry;
            if (geometry.type !== 'Point') {
                continue;
            }

            const radius = getRadius(navaid.properties.power);
            if (!radius) {
                continue;
            }

            const feature = turf.circle(geometry.coordinates, radius, {
                steps: 48,
                units: 'nauticalmiles',
                properties: {
                    id: `${navaid.properties.id}-vor-ring`,
                    vorId: navaid.properties.id,
                    label: `${navaid.properties.id} - ${getRingName(navaid.properties.power)} - ${radius}NM`,
                    type: navaid.properties.type,
                    power: navaid.properties.power
                }
            });

            this.featureCache.set(navaid.properties.id, feature);
            added.push(feature);
        }

        if (added.length > 0) {
            added.forEach((x) => (x.id = x.properties!.id));
            const source = this.map.getSource('fe-navaid-rings') as GeoJSONSource;
            source.updateData({ add: added });
        }
    }
}

function getRadius(power: string) {
    switch (power) {
        case 'T':
            return 25;
        case 'L':
            return 40;
        case 'H':
        case 'U':
            return 130;
        default:
            return null;
    }
}

function getRingName(power: string) {
    switch (power) {
        case 'T':
            return 'Terminal';
        case 'L':
            return 'Low';
        case 'H':
            return 'High';
        case 'U':
            return 'Unrestricted';
        default:
            return null;
    }
}
